import { default as React, ReactNode, useMemo } from "react";
import { IntlProvider, MessageFormatElement } from "react-intl";
import { LanguageContext } from "../../i18n/LanguageContext";
import fixedStrings from "../../../lib/fixed-strings";

// Needed to avoid errors due to emotion's css prop.
import type {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  default as _2
} from "@emotion/core";

import { AnalyticsProvider, SiteArea } from "../../utils/analytics";

import { WindowLocation } from "@reach/router";
import "@reach/skip-nav/styles.css";
import { GlobalMenus } from "../../model/menu";
import { ConsentProvider } from "../../utils/compliance";
import { IsRtlContext } from "../../utils/LayoutContext";
import { LocationProvider } from "../../utils/useGatsbyLocation";
import ModalManagerProvider from "../../utils/useModalManager";
import SessionSettingsProvider from "../../utils/useSessionSettings";

export interface ProvidersProps {
  menus: GlobalMenus;
  children: ReactNode;
  location: WindowLocation;
  messages: Record<string, MessageFormatElement[]>;
  siteArea: SiteArea;
  language: string;
}

const Providers = ({
  language,
  location,
  messages,
  siteArea,
  children
}: ProvidersProps) => {
  language = language || "en";
  // page-translate is a fake locale for Crowdin in-context translation.
  // Use "lol" as the Intl locale (matching Crowdin's internal code) to
  // avoid RangeError from Intl APIs while keeping page-translate in URLs.
  const intlLocale = language === "page-translate" ? "lol" : language;
  const allMessages = useMemo(
    () => ({ ...messages, ...fixedStrings }) as typeof messages,
    [messages]
  );
  const isRtl = dirForLanguage(language) === "rtl";
  return (
    <ConsentProvider>
      <AnalyticsProvider siteArea={siteArea}>
        <LocationProvider location={location}>
          <LanguageContext.Provider value={language}>
            <IsRtlContext.Provider value={isRtl}>
              <SessionSettingsProvider>
                <ModalManagerProvider>
                  <IntlProvider
                    locale={intlLocale}
                    defaultLocale="en"
                    messages={allMessages}
                  >
                    {children}
                  </IntlProvider>
                </ModalManagerProvider>
              </SessionSettingsProvider>
            </IsRtlContext.Provider>
          </LanguageContext.Provider>
        </LocationProvider>
      </AnalyticsProvider>
    </ConsentProvider>
  );
};

export default Providers;

const dirForLanguage = (language: string): string | undefined => {
  switch (language) {
    case "ar":
      return "rtl";
    default:
      return "ltr";
  }
};
